// W13-1b — THE PERMISSION MOMENT. HER RULING: IT APPEARS AUTOMATICALLY ON THE FIRST OPEN OF THE
// INSTALLED APP. No bell-hunting, no extra step.
//
// **THE TRACE THAT PRODUCED THIS FILE (W13-1a): THE MOMENT DID NOT EXIST.** `requestPermission` had
// zero hits in src/, there was no standalone detection anywhere in the client, no push_subscriptions
// table in 61 migrations, no `push` handler in the worker, and `server/web-push.js` — a correct,
// gated RFC-8292 sender — had zero call sites outside itself. Nothing had ever asked her.
//
// **THE ONE PLATFORM TRUTH THAT SHAPES THE DESIGN, AND IT DOES NOT BEND.** `Notification.request-
// Permission()` may only be called from a USER GESTURE; a page is not allowed to raise the browser's
// own prompt by itself, and on iOS web push exists only for an app installed to the home screen
// (16.4+). So her ruling is honoured exactly as written, with the only lawful split there is:
//
//     OUR moment appears automatically.  HER TAP on it is the gesture that raises the browser prompt.
//
// **AND THAT SPLIT IS BETTER THAN THE ALTERNATIVE EVEN WHERE IT IS ALLOWED, FOR A REASON THAT COSTS
// REAL MEMBERS: A DENIED PERMISSION IS PERMANENT AND UNRECOVERABLE IN-APP.** A browser prompt fired
// cold, before she knows what she is being offered, is a coin flip on a decision that cannot be
// asked again from inside the app — she would have to go into browser settings to undo it. Spending
// that one irreversible chance on a cold ask is the actual risk here, not a missing popup.
//
// This module imports NOTHING, so both sides read it and `scripts/w13-push.mjs` RUNS it.

// What she answered. Stored on the account (migration 062) so a second device does not start over.
//
// **P4 ADDED `OFF`, AND IT IS A FOURTH VALUE RATHER THAN A REUSED `later` FOR ONE REASON: R18.**
// A member who dismissed an ask and a member who went into her settings and turned notifications off
// are different facts, and the column is printed VERBATIM on the morning strip and the beacon sweep,
// so collapsing them makes a deliberate refusal read as a shrug. It is safe on every receiving side
// BY CONSTRUCTION, which was measured before it was added rather than hoped for: `pushAnswerValid`
// derives from this object, `shouldOfferPush` refuses on ANY prior answer, `healDecision` acts only
// on `granted`, and the two diagnostic reads hand the raw string on. **The one place that branches on
// specific values is `answerAllowsPush` below (L3), and it is a ROSTER rather than a comparison** —
// which is what keeps a fifth answer a roster entry rather than a hunt through receiving sides whose
// default is silence (R17/R28/R29).
export const PUSH_ANSWERS = { GRANTED: "granted", DENIED: "denied", LATER: "later", OFF: "off" };
export const PUSH_ANSWER_VALUES = Object.values(PUSH_ANSWERS);
export function pushAnswerValid(a) { return PUSH_ANSWER_VALUES.includes(String(a || "")); }

// ---------------------------------------------------------------------------
// L3 — **DOES HER ANSWER ALLOW A PUSH?** Her order, from Michaela's phone: *"no send path filters on
// `push_answer`; the morning roster is 'members with a push subscription row'. Fix at the class."*
//
// **MEASURED FIRST, ON HER LIVE ROWS.** Michaela has `push_answer = 'later'`, one device row since
// 08-01, and **six sends with no beacon of any kind** — two daily lines, two generations, a post and
// a message. `last_seen_at` moved with the newest send, and that column is written ONLY on the push
// service's own `result.ok`, so **the sends were accepted and the phone never answered.** The house
// was buzzing at a member who had said not now, on every path it has.
//
// **AND THE PARAGRAPH ABOVE THIS ONE HAD TO BE CORRECTED IN THE SAME EDIT (R141/R68), BECAUSE IT
// SAID "NOTHING IN THIS REPO BRANCHES ON A SPECIFIC ANSWER VALUE".** That was true when it was
// written and this is the round that makes it false — so the branch is HERE, once, on a roster,
// rather than in four senders' heads.
//
// **THE DIVISION IS R18/R19's AND IT IS DELIBERATELY NOT "ANYTHING BUT GRANTED".** An answer that
// SAYS NO refuses; an answer nobody has ever recorded is NOT a refusal. Every member who predates
// this column has `NULL` in it and has been receiving her morning for weeks — reading absence as no
// would silence the whole house on the day this shipped, which is the one direction a send filter
// may never be wrong in.
export const PUSH_ANSWERS_REFUSING = [PUSH_ANSWERS.DENIED, PUSH_ANSWERS.LATER, PUSH_ANSWERS.OFF];
export const PUSH_ANSWERS_ALLOWING = [PUSH_ANSWERS.GRANTED];

export function answerAllowsPush(answer) {
  const a = String(answer ?? "").trim().toLowerCase();
  if (!a) return true;                                   // never asked, or a member who predates the column
  if (PUSH_ANSWERS_REFUSING.includes(a)) return false;   // she said no, in one of the three ways there are
  return true;                                           // an unrostered value is not a refusal she gave
}

// Is this session inside the INSTALLED app? Both signals, because they disagree by platform:
// `display-mode: standalone` is the standard and is what Android/desktop report; `navigator.standalone`
// is the iOS-only one and is the ONLY signal on the platform that matters most here.
export function isInstalledApp(win) {
  if (!win) return false;
  try {
    if (win.navigator && win.navigator.standalone === true) return true;
    if (typeof win.matchMedia === "function") {
      const mm = win.matchMedia("(display-mode: standalone)");
      if (mm && mm.matches) return true;
    }
  } catch { /* a hostile or partial window is simply not the installed app */ }
  return false;
}

// P4 — **IS THIS AN iOS DEVICE?** Asked for exactly one purpose and consulted at exactly one rung:
// `pushPanelState` reaches it only where push is ALREADY unsupported, to decide between two
// sentences — *"this browser cannot do notifications"* (true, final, no repair) and *"add it to your
// home screen"* (true, one gesture). Getting it wrong in the first direction is the expensive one,
// because it tells an iPhone member there is nothing she can do about a thing she can fix in ten
// seconds. So it OVER-collects on purpose (R19's direction), and the over-collection is bounded by
// the rung it is asked at: a touchscreen Windows laptop and a Mac both support web push, so neither
// ever reaches this question at all.
//
// Three clauses, each a fact rather than a guess. `navigator.standalone` is the iOS-ONLY property
// and exists (as `false`) in iOS Safari whether installed or not; the UA test catches the iOS
// browsers that do not expose it; and the third is iPadOS 13+, which reports a Macintosh UA and
// would otherwise be told, wrongly and finally, that it cannot.
export function isIosDevice(win) {
  if (!win) return false;
  try {
    const nav = win.navigator || {};
    if (nav.standalone !== undefined) return true;
    const ua = String(nav.userAgent || "");
    if (/iPad|iPhone|iPod/i.test(ua)) return true;
    if (/Macintosh/i.test(ua) && Number(nav.maxTouchPoints || 0) > 1) return true;
  } catch { /* a hostile or partial window is not an iPhone */ }
  return false;
}

// Can this browser do web push at all? Asked before anything is shown, so a member on a browser that
// cannot deliver is never offered something that would silently do nothing.
export function pushSupported(win) {
  if (!win) return false;
  return Boolean(win.Notification && win.navigator && win.navigator.serviceWorker && win.PushManager);
}

// ---------------------------------------------------------------------------
// THE DECISION. Her four conditions, ANDed, plus the two the platform adds.
//
// Every condition is named and returned, so the fixture (and a future trace) can ask WHY it stayed
// away rather than watching a boolean and guessing. R17's lesson from the telling end: a silent
// "no" is indistinguishable from a broken feature.
// ---------------------------------------------------------------------------
export function shouldOfferPush({
  installed = false,
  signedIn = false,
  hasRoom = false,
  priorAnswer = null,
  permission = "default",
  supported = true,
  // ITEM 7(a) — **SHE JUST INSTALLED IT, IN THIS MOTION, IN THIS TAB.** Her clause is that the
  // notification ask flows DIRECTLY AFTER the install, and on Android the install lands while she is
  // still standing in the browser tab: `isInstalledApp(window)` is false there, so this refused with
  // *"not the installed app"* and her one warm motion ended at the install.
  //
  // **IT IS ONE CLAUSE ON THE ONE LAW RATHER THAN A SECOND `shouldOfferPushAfterInstall` BESIDE IT**
  // (R28/R29: two answers to *may we ask her* is the shape this house keeps paying for), and **it
  // DEFAULTS TO FALSE, so nothing changes for any existing caller** — the neutrality argument ITEM
  // 12's ring shipped on: a member merely browsing the site in a tab is still refused, exactly as she
  // is today, because the widening is the INSTALL and not the tab.
  //
  // Every other condition still binds, and two of them carry this on their own: `supported` is false
  // in an iOS Safari tab (no `PushManager` outside the installed app), so the iOS half refuses here
  // structurally rather than by a platform test written twice.
  justInstalled = false,
  // >>> INSTALL-1, HER CLAUSE 5 — **DESKTOP NOTIFICATIONS.** <<<
  //
  // **A DESKTOP BROWSER NEEDS NO INSTALLED APP AND NEVER DID.** Chrome, Firefox and Edge do web push
  // from an ordinary tab; there is no home screen to add anything to, and `shouldOfferInstall`
  // refuses desktop outright and correctly (*"a desktop home screen is not what this is for"*). So
  // the installed-app requirement — written for iOS, where web push exists ONLY inside the app — was
  // refusing every desktop member a permission her browser was ready to grant, with the reason
  // *"not the installed app"* naming a thing that does not exist on her machine.
  //
  // **IT IS ONE CLAUSE ON THE ONE LAW, NOT A SECOND `shouldOfferDesktopPush` (R28/R29)**, and it
  // DEFAULTS TO FALSE, so every existing caller is byte-identical (ITEM 12's neutrality argument,
  // and `justInstalled`'s own precedent three lines up).
  //
  // **`supported` STILL BINDS AND IS WHAT MAKES THIS SAFE.** A browser with no `PushManager` is
  // refused by the clause above whatever this one says, so widening here cannot offer an ask that
  // could not be answered — and the iOS half is untouched, because an iOS Safari tab reports no
  // `PushManager` at all.
  desktop = false,
} = {}) {
  const reasons = [];
  if (!supported) reasons.push("this browser cannot do web push");
  if (!installed && justInstalled !== true && desktop !== true) reasons.push("not the installed app");
  if (!signedIn) reasons.push("not signed in");
  if (!hasRoom) reasons.push("no room yet");
  // The BROWSER's own state outranks ours: if she has already granted or denied at the browser
  // level, there is nothing left to ask and asking again is noise.
  if (permission === "granted") reasons.push("already granted at the browser");
  if (permission === "denied") reasons.push("denied at the browser, and that cannot be re-asked in-app");
  // Our record. "later" is REMEMBERED and is not a denial: it stops the nag.
  //
  // **THIS LINE USED TO END "and the bell in My Room stays as the manual way in (her ruling: the
  // bell remains as the fallback)", AND THAT DOOR DID NOT EXIST** — measured by P4, which was sent
  // to retire it. Nothing in `src/` had ever rendered a manual push control, so a member who tapped
  // "Not now" hit this refusal on every later launch and had nowhere to go: **"not now" was
  // permanent.** The fallback is real now and it is named rather than described —
  // `src/components/PushSettings.tsx` in the account menu, on every page.
  if (priorAnswer && pushAnswerValid(priorAnswer)) reasons.push(`already answered: ${priorAnswer}`);
  return { offer: reasons.length === 0, reasons };
}

// The words on the sheet. Kept here so the copy is DATA a gate can read and she can change in one
// place, and deliberately plain: it says what will be sent and what will not.
export const PUSH_COPY = {
  title: "Want a nudge when something is ready?",
  body: "We can let you know when a reading you asked for is finished, and when someone answers you in the Living Room. Nothing else, and you can turn it off whenever you like.",
  accept: "Yes, let me know",
  later: "Not now",
};

// The shape the browser hands back, flattened to what the server stores. Kept as a function so the
// client cannot invent a fourth field and the server cannot be surprised by one.
export function subscriptionPayload(sub) {
  if (!sub) return null;
  const json = typeof sub.toJSON === "function" ? sub.toJSON() : sub;
  const endpoint = String(json?.endpoint || "");
  const p256dh = String(json?.keys?.p256dh || "");
  const auth = String(json?.keys?.auth || "");
  if (!endpoint || !p256dh || !auth) return null;
  return { endpoint, p256dh, auth };
}

// The VAPID public key arrives base64url and `pushManager.subscribe` wants raw bytes.
export function urlBase64ToUint8Array(base64) {
  const s = String(base64 || "").trim();
  if (!s) return new Uint8Array(0);
  const padded = (s + "=".repeat((4 - (s.length % 4)) % 4)).replace(/-/g, "+").replace(/_/g, "/");
  const raw = typeof atob === "function" ? atob(padded) : Buffer.from(padded, "base64").toString("binary");
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) out[i] = raw.charCodeAt(i);
  return out;
}
